import { getKnownCardAnchors, MAIN_CONFIG_TAB_KEYS, SUB_CRUMB_KEY } from '../screens/webshop/search/WebshopConfigSearch';

// Dynamic permission CATALOG, derived from artifacts the codebase already
// maintains (the search index) so it auto-grows: add a card anchor / search
// route and it becomes a gateable node here + in the role-config tree, with no
// second hand-maintained list.
//
// Node hierarchy (parentKey links): screen → main config tab → card. Plus
// opt-in field nodes. `can()` (in Permissions.js) uses the parent/child maps;
// labels (needing `t`) are only built for the dialog.
//
// Key scheme (kept compatible with the existing call sites):
//   webshop.screen.<x>            screens (sidebar entries)
//   webshop.config.tab.<x>        main config tabs
//   webshop.config.group.<g>      a section/modal group under a tab (folder)
//   webshop.config.card.<tail>    a card; <tail> = anchor minus "cfg-card-"
//   webshop.field.<x>             opt-in individual field

const SCREEN_NODES = [
    { key: 'webshop.screen.config',      label: 'Configuratie' },
    { key: 'webshop.screen.data',        label: 'Data' },
    { key: 'webshop.screen.connections', label: 'Connecties' },
    { key: 'webshop.screen.actions',     label: 'Acties' },
];

// Main config tabs (under the Configuratie screen). Index = main tab index, so
// `getKnownCardAnchors().main` maps a card onto its tab. Labels reuse the SAME
// i18n keys the tabs themselves use (MAIN_CONFIG_TAB_KEYS).
const MAIN_TAB_KEYS = [
    'webshop.config.tab.pages',
    'webshop.config.tab.style',
    'webshop.config.tab.features',
    'webshop.config.tab.processing',
    'webshop.config.tab.sync',
];
const MAIN_TAB_FALLBACK = ['Pages', 'Style', 'Features', 'Processing', 'Sync'];

// Opt-in field nodes — the framework supports field-level gating everywhere;
// these are the explicitly-wired ones. Add an entry + a can() call site to gate
// a new field.
const FIELD_NODES = [
    { key: 'webshop.field.dbCredentials', parent: 'webshop.screen.connections', label: 'Database-credentials (Connecties)' },
];

export const cardKeyForAnchor = (anchor) =>
    anchor ? 'webshop.config.card.' + String(anchor).replace(/^cfg-card-/, '') : null;

// Last segment of an "A › B › C" crumb — a concise label.
const lastSegment = (crumb) => {
    const parts = String(crumb || '').split('›').map(s => s.trim()).filter(Boolean);
    return parts.length ? parts[parts.length - 1] : String(crumb || '');
};

// Humanised anchor tail — safety-net card label if the title key is missing
// (e.g. "cfg-card-menu-header-content" → "Menu header content").
const humanizeTail = (anchor) => {
    const tail = String(anchor || '').replace(/^cfg-card-/, '').replace(/-/g, ' ').trim();
    return tail ? tail.charAt(0).toUpperCase() + tail.slice(1) : String(anchor || '');
};

// English fallback names for the Style modal sections (used when the
// `cfg_style_section_<section>` i18n key isn't present).
const STYLE_SECTION_FALLBACK = {
    menu: 'Menu bar', footer: 'Footer', footerExtra: 'Footer (extra)', header: 'Header (logo)',
    productDetailInfo: 'Product detail info', productCard: 'Product card', groups: 'Filters & sorting',
    grid: 'Product grid', image: 'Image gallery', globalWidths: 'Global widths', language: 'Taalbalk',
};

// The section/modal group a card belongs to: a stable (label-independent) key +
// how to translate its label. Style cards group by their modal section; every
// other card groups by its sub-tab (SUB_CRUMB_KEY). Returns null if a card has
// neither (then it hangs directly under its main tab).
const groupOf = (c) => {
    if (c.modal && c.modal.section) {
        const s = c.modal.section;
        return { key: 'webshop.config.group.style.' + s, labelKey: 'cfg_style_section_' + s, fallback: STYLE_SECTION_FALLBACK[s] || s };
    }
    if (c.sub) {
        const [k, v] = Object.entries(c.sub)[0];
        return { key: 'webshop.config.group.' + k + '.' + v, labelKey: (SUB_CRUMB_KEY[k] || [])[v] || null, fallback: lastSegment(c.crumb) };
    }
    return null;
};

// Cards whose real title key doesn't follow the "<route-prefix>title" pattern
// (the section title == the prefix itself, or a differently-suffixed key).
const ANCHOR_TITLE_KEY = {
    'cfg-card-proc-pickup-stores':       'cfg_proc_ship_pickup_stores_title',
    'cfg-card-voucher-layouts-physical': 'cfg_voucher_layouts_physical',
    'cfg-card-voucher-layouts-digital':  'cfg_voucher_layouts_digital',
    'cfg-card-voucher-layouts-defaults': 'cfg_voucher_layouts_defaults',
    'cfg-card-dyn-title':                'cfg_dyn_title_card',
    'cfg-card-groups-default':           'cfg_groups_default_sort_title',
    'cfg-card-pdi-title-url':            'cfg_pdi_title_url_title',
    'cfg-card-menu-header-content':      'cfg_nav_header_content_title',
    'cfg-card-language-bar':             'cfg_lang_config_title',
};

// The i18n key for a card's own title: an explicit override, else its
// title-prefix route + "title".
const cardTitleKey = (c) => ANCHOR_TITLE_KEY[c.anchor] || (c.prefix ? c.prefix + 'title' : null);

// ── Static structure (label-independent), computed once at module load ───────
const CARD_ANCHORS = getKnownCardAnchors();

// Collapse single-card groups: a folder holding exactly one card is noise, so
// that card hangs directly under its tab instead of inside a same-named folder.
const GROUP_CARD_COUNT = (() => {
    const m = {};
    CARD_ANCHORS.forEach(c => { const g = groupOf(c); if (g) m[g.key] = (m[g.key] || 0) + 1; });
    return m;
})();
const effectiveGroupOf = (c) => {
    const g = groupOf(c);
    return (g && GROUP_CARD_COUNT[g.key] > 1) ? g : null;
};

export const CATALOG_PARENTS = (() => {
    const parents = {};
    SCREEN_NODES.forEach(s => { parents[s.key] = null; });
    MAIN_TAB_KEYS.forEach(k => { parents[k] = 'webshop.screen.config'; });
    // Section/modal groups (folders) hang under their main tab...
    CARD_ANCHORS.forEach(c => {
        const g = effectiveGroupOf(c);
        if (g && !(g.key in parents)) parents[g.key] = MAIN_TAB_KEYS[c.main] || 'webshop.screen.config';
    });
    // ...and each card hangs under its group (or its tab if it has none).
    CARD_ANCHORS.forEach(c => {
        const g = effectiveGroupOf(c);
        parents[cardKeyForAnchor(c.anchor)] = g ? g.key : (MAIN_TAB_KEYS[c.main] || 'webshop.screen.config');
    });
    FIELD_NODES.forEach(f => { parents[f.key] = f.parent; });
    return parents;
})();

export const ALL_CATALOG_KEYS = Object.keys(CATALOG_PARENTS);

export const CATALOG_CHILDREN = (() => {
    const children = {};
    Object.entries(CATALOG_PARENTS).forEach(([key, parent]) => {
        if (parent) (children[parent] = children[parent] || []).push(key);
    });
    return children;
})();

// All descendant keys of a key (depth-first).
export const descendantsOf = (key) => {
    const out = [];
    const stack = [...(CATALOG_CHILDREN[key] || [])];
    while (stack.length) {
        const k = stack.pop();
        out.push(k);
        (CATALOG_CHILDREN[k] || []).forEach(c => stack.push(c));
    }
    return out;
};

// A node's own key + all its descendants — handy for granting a whole branch.
export const subtreeKeys = (key) => [key, ...descendantsOf(key)];

// ── Labelled nodes for the dialog (needs `t`) ────────────────────────────────
export const buildCatalogNodes = (t) => {
    const tr = (key, fb) => {
        if (typeof t !== 'function' || !key) return fb;
        const v = t(key);
        return (v && v !== key) ? v : fb;
    };
    const nodes = [];
    SCREEN_NODES.forEach(s => nodes.push({ key: s.key, label: s.label, parentKey: null, kind: 'screen' }));
    MAIN_TAB_KEYS.forEach((k, i) => nodes.push({
        key: k, label: tr(MAIN_CONFIG_TAB_KEYS[i], MAIN_TAB_FALLBACK[i]),
        parentKey: 'webshop.screen.config', kind: 'tab',
    }));
    // Section/modal group folders (one per unique group, first card wins its label).
    const groupSeen = new Set();
    CARD_ANCHORS.forEach(c => {
        const g = effectiveGroupOf(c);
        if (!g || groupSeen.has(g.key)) return;
        groupSeen.add(g.key);
        nodes.push({
            key: g.key, label: tr(g.labelKey, g.fallback),
            parentKey: MAIN_TAB_KEYS[c.main] || 'webshop.screen.config', kind: 'group',
        });
    });
    // Cards, labelled with their own (translated) title; humanised tail as a net.
    CARD_ANCHORS.forEach(c => {
        const g = effectiveGroupOf(c);
        nodes.push({
            key: cardKeyForAnchor(c.anchor),
            label: tr(cardTitleKey(c), humanizeTail(c.anchor)),
            crumb: c.crumb,
            parentKey: g ? g.key : (MAIN_TAB_KEYS[c.main] || 'webshop.screen.config'),
            kind: 'card',
        });
    });
    FIELD_NODES.forEach(f => nodes.push({ key: f.key, label: f.label, parentKey: f.parent, kind: 'field' }));
    return nodes;
};
